import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import SEO from '@/components/SEO';
import axios from 'axios';
import { API_URL } from '@/lib/utils';
import { 
  Target, Eye, Award, Users, Building, Clock, 
  CheckCircle, ArrowRight, Shield, Star
} from 'lucide-react';

// High-quality healthcare images
const IMAGES = {
  teamMeeting: "https://images.pexels.com/photos/5452193/pexels-photo-5452193.jpeg?auto=compress&cs=tinysrgb&w=1200",
  consulting: "https://images.pexels.com/photos/5452285/pexels-photo-5452285.jpeg?auto=compress&cs=tinysrgb&w=1200",
  hospital: "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=1200&h=800&fit=crop",
  medical: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=1200&h=800&fit=crop"
};

const values = [
  { icon: Shield, title: 'Integrity', desc: 'We maintain the highest ethical standards in all our engagements.' },
  { icon: Award, title: 'Excellence', desc: 'We strive for excellence in every project we undertake.' },
  { icon: Users, title: 'Client Focus', desc: 'Our clients success is our primary measure of achievement.' },
  { icon: Target, title: 'Innovation', desc: 'We continuously innovate to provide cutting-edge solutions.' },
];

const About = () => {
  const [stats, setStats] = useState([
    { value: '200+', label: 'Healthcare Facilities', icon: Building },
    { value: '8+', label: 'Years Experience', icon: Clock },
    { value: '100%', label: 'Success Rate', icon: Star },
    { value: '15+', label: 'Expert Consultants', icon: Users },
  ]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/settings`);
        const s = res.data;
        setStats([
          { value: s.stat_facilities || '200+', label: s.stat_facilities_label || 'Healthcare Facilities', icon: Building },
          { value: s.stat_experience_years || '8+', label: s.stat_experience_label || 'Years Experience', icon: Clock },
          { value: s.stat_success_rate || '100%', label: s.stat_success_label || 'Success Rate', icon: Star },
          { value: s.stat_consultants || '15+', label: s.stat_consultants_label || 'Expert Consultants', icon: Users },
        ]);
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);
  return (
    <div data-testid="about-page">
      <SEO 
        title="About Us - Healthcare Consulting Experts"
        description="Learn about Steward Healthcare India - Leading healthcare consulting company since 2016. 200+ healthcare facilities served with 15+ expert consultants. Provisional NABH Mitra certified."
        keywords="about steward healthcare, healthcare consulting company India, NABH consultants, healthcare experts Panipat"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-blue-50 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#2eb24c] font-semibold text-sm uppercase tracking-wider">About Us</span>
              <h1 className="font-manrope text-4xl md:text-5xl font-bold text-[#1a2a5e] mt-2 mb-6">
                Transforming Healthcare Through Excellence
              </h1>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                Steward Healthcare India Pvt. Ltd. is a leading healthcare consulting company dedicated to helping healthcare organizations achieve operational excellence, quality accreditation, and digital transformation.
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                With a proven track record and a team of certified professionals, we have successfully supported healthcare facilities across India in their journey towards quality and excellence.
              </p>
              <Button asChild size="lg" className="bg-[#1a2a5e] hover:bg-[#2d4a8e]">
                <Link to="/contact">
                  Get in Touch <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
            <div className="relative">
              <img 
                src={IMAGES.teamMeeting}
                alt="Healthcare team" 
                className="rounded-2xl shadow-2xl w-full h-[450px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-[#1a2a5e] py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center text-white">
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <stat.icon className="h-6 w-6 text-[#2eb24c]" />
                </div>
                <div className="text-3xl md:text-4xl font-bold font-manrope">{stat.value}</div>
                <div className="text-blue-200 text-sm mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-0 shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-[#1a2a5e] to-[#3b7dd8] p-6">
                <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center mb-4">
                  <Eye className="h-7 w-7 text-white" />
                </div>
                <h2 className="font-manrope text-2xl font-bold text-white">Our Vision</h2>
              </div>
              <CardContent className="p-6">
                <p className="text-gray-600 leading-relaxed">
                  To be India's most trusted healthcare consulting partner, driving quality, innovation, and excellence across the healthcare ecosystem. We envision a future where every healthcare facility in India delivers world-class patient care through robust quality systems and cutting-edge technology.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-[#2eb24c] to-[#3b7dd8] p-6">
                <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center mb-4">
                  <Target className="h-7 w-7 text-white" />
                </div>
                <h2 className="font-manrope text-2xl font-bold text-white">Our Mission</h2>
              </div>
              <CardContent className="p-6">
                <p className="text-gray-600 leading-relaxed">
                  To empower healthcare organizations with comprehensive consulting services, innovative digital solutions, and continuous support that enables them to achieve accreditation excellence, operational efficiency, and superior patient outcomes while maintaining sustainable growth.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-[#2eb24c] font-semibold text-sm uppercase tracking-wider">Why Choose Us</span>
            <h2 className="font-manrope text-3xl md:text-4xl font-bold text-[#1a2a5e] mt-2">
              What Sets Us Apart
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { title: 'Industry Expertise', desc: 'Our team comprises certified healthcare professionals with hands-on experience in hospital management, quality assurance, and healthcare IT.', icon: Award },
              { title: 'End-to-End Solutions', desc: 'From initial gap analysis to final accreditation and beyond, we provide comprehensive support at every stage of your quality journey.', icon: CheckCircle },
              { title: 'Proven Track Record', desc: 'With 500+ successful projects and 100% accreditation success rate, our expertise is backed by results.', icon: Star },
              { title: 'Customized Approach', desc: 'We understand that every healthcare facility is unique. Our solutions are tailored to your specific needs and goals.', icon: Target },
              { title: 'Technology Integration', desc: 'We seamlessly integrate modern technology solutions to enhance your healthcare delivery and operational efficiency.', icon: Building },
              { title: 'Continuous Support', desc: 'Our relationship doesnt end with accreditation. We provide ongoing support to ensure sustained excellence.', icon: Users },
            ].map((item, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-[#1a2a5e]/10 rounded-xl flex items-center justify-center mb-4">
                    <item.icon className="h-6 w-6 text-[#1a2a5e]" />
                  </div>
                  <h3 className="font-manrope font-bold text-lg text-[#1a2a5e] mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-[#2eb24c] font-semibold text-sm uppercase tracking-wider">Our Values</span>
            <h2 className="font-manrope text-3xl md:text-4xl font-bold text-[#1a2a5e] mt-2">
              Core Values That Drive Us
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div key={index} className="text-center p-6">
                <div className="w-16 h-16 bg-[#2eb24c]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <value.icon className="h-8 w-8 text-[#2eb24c]" />
                </div>
                <h3 className="font-manrope font-bold text-lg text-[#1a2a5e] mb-2">{value.title}</h3>
                <p className="text-gray-600 text-sm">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1a2a5e] text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-manrope text-3xl md:text-4xl font-bold mb-6">
            Ready to Partner with Us?
          </h2>
          <p className="text-blue-100 text-lg mb-8">
            Let's discuss how we can help you achieve healthcare excellence.
          </p>
          <Button asChild size="lg" className="bg-[#2eb24c] hover:bg-[#259a40] h-14 px-8">
            <Link to="/contact">
              Contact Us Today <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default About;
